<template>
  <div class="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 pb-20">
    <!-- Sticky Header -->
    <header class="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-gray-200/50 shadow-sm">
      <div class="max-w-[1760px] mx-auto px-4 sm:px-6 md:px-10 lg:px-20">
        <div class="flex items-center justify-between h-16 md:h-20">
          <!-- Logo -->
          <a href="/" class="flex items-center gap-2.5 group">
            <div class="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
              <svg class="w-5 h-5 md:w-6 md:h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
            <span class="text-lg md:text-xl font-bold tracking-tight text-gray-900 font-premium">Tickets</span>
          </a>

          <!-- Right Menu -->
          <div class="flex items-center gap-2 md:gap-3">
            <a href="/bookings" class="hidden sm:flex px-4 py-2 text-sm text-violet-700 font-semibold rounded-xl bg-violet-50 transition-all duration-200">
              Mes réservations
            </a>
            <div class="w-9 h-9 md:w-11 md:h-11 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-md">
              <span class="text-white font-bold text-sm md:text-base">JD</span>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Page Title -->
    <div class="max-w-[1760px] mx-auto px-4 sm:px-6 md:px-10 lg:px-20 pt-8 md:pt-12">
      <h1 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-premium">
        Mes réservations
      </h1>
    </div>

    <!-- Premium Tabs -->
    <div class="bg-white border-b border-gray-200/50">
      <div class="max-w-[1760px] mx-auto px-4 sm:px-6 md:px-10 lg:px-20">
        <div class="flex gap-2 md:gap-4">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            class="py-4 px-4 md:px-6 text-sm font-medium rounded-t-2xl transition-all duration-300 relative"
            :class="activeTab === tab.id
              ? 'text-violet-700 bg-gradient-to-b from-violet-50 to-transparent'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'"
          >
            <div class="flex items-center gap-2">
              {{ tab.label }}
              <span
                v-if="tab.count > 0"
                class="px-2 py-0.5 rounded-full text-xs font-semibold"
                :class="activeTab === tab.id
                  ? 'bg-violet-600 text-white'
                  : 'bg-gray-200 text-gray-600'"
              >
                {{ tab.count }}
              </span>
            </div>
            <div
              v-if="activeTab === tab.id"
              class="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-t-2xl"
            ></div>
          </button>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-[1760px] mx-auto px-4 sm:px-6 md:px-10 lg:px-20 py-8 md:py-12">
      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-20">
        <div class="flex flex-col items-center gap-4">
          <div class="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin"></div>
          <p class="text-gray-600">Chargement de vos réservations...</p>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="text-center py-12">
        <div class="inline-flex items-center gap-2 px-6 py-3 bg-red-50 text-red-700 rounded-2xl mb-6">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
          </svg>
          {{ error }}
        </div>
        <button
          @click="loadBookings"
          class="px-8 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
        >
          Réessayer
        </button>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredBookings.length === 0" class="text-center py-20">
        <div class="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
          <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
          </svg>
        </div>
        <h3 class="text-2xl font-bold text-gray-900 mb-3 font-premium">
          Aucune réservation
        </h3>
        <p class="text-lg text-gray-600 mb-8 max-w-md mx-auto">
          {{ getEmptyMessage() }}
        </p>
        <button
          v-if="activeTab === 'upcoming'"
          @click="goToExplore"
          class="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold rounded-2xl shadow-xl hover:shadow-2xl hover:from-violet-700 hover:to-indigo-700 transform hover:scale-[1.02] transition-all duration-300"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          Explorer les événements
        </button>
      </div>

      <!-- Bookings Grid -->
      <div v-else class="space-y-8">
        <!-- Group by date -->
        <div v-for="(group, date) in groupedBookings" :key="date">
          <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-5 flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            {{ date }}
          </h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
            <div
              v-for="booking in group"
              :key="booking.id"
              @click="viewBooking(booking.id)"
              class="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group hover:-translate-y-1"
            >
              <!-- Image -->
              <div class="aspect-[4/3] relative overflow-hidden">
                <img
                  :src="booking.ticketImage"
                  :alt="booking.ticketTitle"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <!-- Status Badge -->
                <div class="absolute top-4 left-4 px-4 py-1.5 rounded-full text-xs font-semibold shadow-lg backdrop-blur-sm" :class="getStatusClass(booking.status)">
                  {{ getStatusLabel(booking.status) }}
                </div>
                <!-- Overlay on hover -->
                <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              <!-- Content -->
              <div class="p-5">
                <h4 class="text-lg font-semibold text-gray-900 mb-3 line-clamp-2 group-hover:text-violet-700 transition-colors duration-300 font-premium">
                  {{ booking.ticketTitle }}
                </h4>

                <div class="space-y-2 mb-5">
                  <div class="flex items-center gap-2.5 text-sm text-gray-600">
                    <div class="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                      <svg class="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                    </div>
                    <span>{{ booking.date }}</span>
                  </div>
                  <div class="flex items-center gap-2.5 text-sm text-gray-600">
                    <div class="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                      <svg class="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <span>{{ booking.time }}</span>
                  </div>
                  <div class="flex items-center gap-2.5 text-sm text-gray-600">
                    <div class="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                      <svg class="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                    </div>
                    <span class="truncate">{{ booking.venue }}</span>
                  </div>
                </div>

                <!-- Quantity & Price -->
                <div class="flex items-center justify-between py-3 border-t border-gray-100">
                  <span class="text-sm text-gray-500">
                    {{ booking.quantity }} ticket{{ booking.quantity > 1 ? 's' : '' }}
                  </span>
                  <span class="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-indigo-600">
                    {{ booking.totalPrice }}€
                  </span>
                </div>

                <!-- Actions -->
                <div class="flex gap-2 mt-4">
                  <button
                    @click.stop="downloadTicket(booking.id)"
                    class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:border-violet-400 hover:text-violet-700 hover:bg-violet-50 transition-all duration-300"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                    </svg>
                    <span class="hidden sm:inline">Télécharger</span>
                  </button>
                  <button
                    @click.stop="showQRCode(booking.id)"
                    class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:border-violet-400 hover:text-violet-700 hover:bg-violet-50 transition-all duration-300"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"/>
                    </svg>
                    <span class="hidden sm:inline">QR Code</span>
                  </button>
                  <button
                    v-if="booking.status === 'confirmed'"
                    @click.stop="cancelBooking(booking.id)"
                    class="px-3 py-2.5 border-2 border-red-200 text-red-600 rounded-xl hover:bg-red-50 hover:border-red-400 transition-all duration-300"
                    title="Annuler"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const activeTab = ref('upcoming');
const loading = ref(false);
const error = ref(null);

const tabs = [
  { id: 'upcoming', label: 'À venir', count: 0 },
  { id: 'past', label: 'Passés', count: 0 },
  { id: 'cancelled', label: 'Annulés', count: 0 },
];

// Mock bookings
const bookings = ref([
  {
    id: 'BK-001',
    ticketTitle: 'Concert de Stade de France - Soirée Exceptionnelle',
    ticketImage: 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=800',
    date: '15 Juin 2025',
    time: '20h00',
    venue: 'Stade de France',
    city: 'Saint-Denis',
    quantity: 2,
    totalPrice: 170,
    status: 'confirmed',
    orderDate: '2025-01-07',
  },
  {
    id: 'BK-002',
    ticketTitle: 'Match PSG vs OM - Classique intense',
    ticketImage: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800',
    date: '22 Juin 2025',
    time: '21h00',
    venue: 'Parc des Princes',
    city: 'Paris',
    quantity: 1,
    totalPrice: 120,
    status: 'confirmed',
    orderDate: '2025-01-07',
  },
  {
    id: 'BK-003',
    ticketTitle: 'Festival de Musique Électro - Nuit magique',
    ticketImage: 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=800',
    date: '5 Décembre 2024',
    time: '18h00',
    venue: 'Parc de la Tête d\'Or',
    city: 'Lyon',
    quantity: 3,
    totalPrice: 195,
    status: 'used',
    orderDate: '2024-11-15',
  },
  {
    id: 'BK-004',
    ticketTitle: 'Concert Accor Arena - Evening',
    ticketImage: 'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=800',
    date: '10 Juillet 2025',
    time: '19h30',
    venue: 'Accor Arena',
    city: 'Paris',
    quantity: 2,
    totalPrice: 180,
    status: 'confirmed',
    orderDate: '2025-01-05',
  },
]);

const filteredBookings = computed(() => {
  const now = new Date();
  return bookings.value.filter(booking => {
    const bookingDate = new Date(booking.date.split(' ').reverse().join('/'));

    if (activeTab.value === 'upcoming') {
      return booking.status === 'confirmed' && bookingDate > now;
    } else if (activeTab.value === 'past') {
      return booking.status === 'used' || (booking.status === 'confirmed' && bookingDate <= now);
    } else if (activeTab.value === 'cancelled') {
      return booking.status === 'cancelled';
    }
    return true;
  });
});

const groupedBookings = computed(() => {
  const groups = {};
  filteredBookings.value.forEach(booking => {
    const date = booking.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(booking);
  });
  return groups;
});

// Update tab counts
const updateTabCounts = () => {
  const now = new Date();
  tabs.forEach(tab => {
    if (tab.id === 'upcoming') {
      tab.count = bookings.value.filter(b => {
        const bookingDate = new Date(b.date.split(' ').reverse().join('/'));
        return b.status === 'confirmed' && bookingDate > now;
      }).length;
    } else if (tab.id === 'past') {
      tab.count = bookings.value.filter(b => {
        const bookingDate = new Date(b.date.split(' ').reverse().join('/'));
        return b.status === 'used' || (b.status === 'confirmed' && bookingDate <= now);
      }).length;
    } else if (tab.id === 'cancelled') {
      tab.count = bookings.value.filter(b => b.status === 'cancelled').length;
    }
  });
};

const getStatusClass = (status) => {
  switch (status) {
    case 'confirmed':
      return 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white';
    case 'used':
      return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
    case 'cancelled':
      return 'bg-gradient-to-r from-red-500 to-rose-500 text-white';
    default:
      return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'confirmed':
      return 'Confirmé';
    case 'used':
      return 'Utilisé';
    case 'cancelled':
      return 'Annulé';
    default:
      return status;
  }
};

const getEmptyMessage = () => {
  switch (activeTab.value) {
    case 'upcoming':
      return 'Vous n\'avez aucune réservation à venir pour le moment.';
    case 'past':
      return 'Vous n\'avez aucune réservation passée.';
    case 'cancelled':
      return 'Vous n\'avez aucune réservation annulée.';
    default:
      return 'Aucune réservation trouvée.';
  }
};

const viewBooking = (bookingId) => {
  console.log('View booking:', bookingId);
  // Navigate to booking detail
};

const downloadTicket = (bookingId) => {
  console.log('Download ticket:', bookingId);
  // Download logic
};

const showQRCode = (bookingId) => {
  console.log('Show QR code:', bookingId);
  // Show QR modal
};

const cancelBooking = (bookingId) => {
  if (confirm('Êtes-vous sûr de vouloir annuler cette réservation?')) {
    const booking = bookings.value.find(b => b.id === bookingId);
    if (booking) {
      booking.status = 'cancelled';
      updateTabCounts();
    }
  }
};

const loadBookings = () => {
  loading.value = true;
  setTimeout(() => {
    loading.value = false;
  }, 800);
};

const goToExplore = () => {
  router.push('/');
};

onMounted(() => {
  loadBookings();
  updateTabCounts();
});
</script>
